import React, { useState, useRef } from 'react';
import { 
  MessageSquare, 
  Star, 
  Send, 
  FileText,
  Lightbulb, 
  Bug, 
  Heart,
  Users,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle,
  Upload,
  X,
  AlertCircle,
  Smile,
  Meh,
  Frown
} from 'lucide-react';
import MediCureOnSidebar from '../common/MediCureOnSidebar';

const Feedback = ({ onNavigate }) => {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [priority, setPriority] = useState('medium');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [satisfactionScore, setSatisfactionScore] = useState('');
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'general', name: 'General Feedback', icon: MessageSquare, color: 'blue' },
    { id: 'features', name: 'Feature Requests', icon: Lightbulb, color: 'green' },
    { id: 'bugs', name: 'Bug Reports', icon: Bug, color: 'red' },
    { id: 'healthcare', name: 'Healthcare Experience', icon: Heart, color: 'pink' },
    { id: 'community', name: 'Community Features', icon: Users, color: 'purple' },
    { id: 'performance', name: 'Performance Issues', icon: TrendingUp, color: 'orange' },
    { id: 'security', name: 'Security Concerns', icon: Shield, color: 'gray' }
  ];

  const feedbackTypes = [
    { id: 'suggestion', name: 'Suggestion', icon: Lightbulb },
    { id: 'improvement', name: 'Improvement Idea', icon: TrendingUp },
    { id: 'complaint', name: 'Complaint', icon: AlertCircle },
    { id: 'compliment', name: 'Compliment', icon: Heart },
    { id: 'question', name: 'Question', icon: MessageSquare }
  ];

  const priorityLevels = [
    { id: 'low', name: 'Low Priority', color: 'green', description: 'Nice to have improvement' },
    { id: 'medium', name: 'Medium Priority', color: 'yellow', description: 'Should be addressed soon' },
    { id: 'high', name: 'High Priority', color: 'orange', description: 'Important issue affecting experience' },
    { id: 'urgent', name: 'Urgent', color: 'red', description: 'Critical issue requiring immediate attention' }
  ];

  const satisfactionOptions = [
    { id: 'very-satisfied', name: 'Very Satisfied', icon: Smile, color: 'green' },
    { id: 'satisfied', name: 'Satisfied', icon: Smile, color: 'blue' },
    { id: 'neutral', name: 'Neutral', icon: Meh, color: 'gray' },
    { id: 'dissatisfied', name: 'Dissatisfied', icon: Frown, color: 'orange' },
    { id: 'very-dissatisfied', name: 'Very Dissatisfied', icon: Frown, color: 'red' }
  ];

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => 
      file.size <= 10 * 1024 * 1024 && // 10MB limit
      ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'].includes(file.type)
    );
    
    setAttachments(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare comprehensive feedback data for Azure Function
      const feedbackData = {
        rating,
        category: selectedCategory,
        feedbackType,
        priority,
        satisfactionScore,
        title: title.trim(),
        comments: `${title.trim()}${title.trim() && description.trim() ? '\n\n' : ''}${description.trim()}`.trim(),
        userEmail: email.trim() || 'anonymous',
        attachmentCount: attachments.length,
        timestamp: new Date().toISOString(),
        metadata: {
          categoryName: categories.find(c => c.id === selectedCategory)?.name,
          feedbackTypeName: feedbackTypes.find(ft => ft.id === feedbackType)?.name,
          priorityName: priorityLevels.find(p => p.id === priority)?.name,
          satisfactionName: satisfactionOptions.find(s => s.id === satisfactionScore)?.name
        }
      };

      console.log('Comprehensive Feedback Submission:', feedbackData);
      
      // Call Azure Functions endpoint
      const apiBaseUrl = 'https://medicureon-app-api.azurewebsites.net/api';
      
      console.log('Calling API:', `${apiBaseUrl}/feedback/submit`);
      
      const response = await fetch(`${apiBaseUrl}/feedback/submit?code=2o4yEMXQVV7g8d6Lu0b5PtoiKjpKxBk2cmVMbKst5qhvAzFuKNjQjg==`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(feedbackData)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('API Error:', errorData);
        throw new Error(errorData.message || 'Failed to submit feedback');
      }

      const result = await response.json();
      console.log('Feedback submitted successfully:', result);
      
      // Simulate API delay for demo
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSubmitting(false);
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setTitle('');
        setDescription('');
        setEmail('');
        setRating(0);
        setAttachments([]);
        setSatisfactionScore('');
        setSelectedCategory('general');
        setFeedbackType('suggestion');
        setPriority('medium');
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setIsSubmitting(false);
      
      // Show more specific error message
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        alert('Network error: Unable to connect to feedback service. Please check your internet connection and try again.');
      } else {
        alert(`Failed to submit feedback: ${error.message}. Please try again.`);
      }
    }
  };

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
  const CategoryIcon = selectedCategoryData?.icon || MessageSquare;

  if (submitted) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <MediCureOnSidebar onNavigate={onNavigate} activePage="feedback" />
        
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="p-8 text-center bg-white rounded-2xl shadow-lg max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600">Your feedback has been submitted successfully.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <MediCureOnSidebar onNavigate={onNavigate} activePage="feedback" />

      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="flex-shrink-0 shadow-sm border-b border-blue-800" style={{ backgroundColor: '#02276F' }}>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl shadow-lg">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Feedback Center</h1>
                  <p className="text-sm text-blue-100">Help us improve your healthcare experience • Your voice matters</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-white">Response Time</p>
                  <p className="text-xs text-blue-200">Usually within 24 hours</p>
                </div>
                <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                  <Clock className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Categories */}
          <div className="flex flex-col flex-shrink-0 w-80 h-full overflow-hidden bg-white border-r border-gray-200">
            <div className="flex-shrink-0 p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Feedback Categories</h2>
              <p className="text-sm text-gray-600">Choose the category that best describes your feedback</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {categories.map((category) => {
                const Icon = category.icon;
                const isSelected = selectedCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 ${
                      isSelected
                        ? `border-${category.color}-500 bg-${category.color}-50 shadow-md transform scale-105`
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        isSelected ? `bg-${category.color}-100` : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          isSelected ? `text-${category.color}-600` : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className={`font-medium ${
                          isSelected ? `text-${category.color}-900` : 'text-gray-900'
                        }`}>
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Form Area */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              {/* Category Header */}
              <div className="mb-8 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`p-3 bg-${selectedCategoryData?.color}-100 rounded-xl`}>
                    <CategoryIcon className={`w-6 h-6 text-${selectedCategoryData?.color}-600`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedCategoryData?.name}</h2>
                    <p className="text-gray-600">Share your thoughts and help us improve</p>
                  </div>
                </div>
              </div>

              {/* Feedback Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Rating Section */}
                <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">How would you rate your experience?</h3>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${
                            star <= (hoveredRating || rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    {rating > 0 && (
                      <span className="ml-3 text-sm text-gray-600">
                        {rating === 5 ? 'Excellent' : 
                         rating === 4 ? 'Good' : 
                         rating === 3 ? 'Average' : 
                         rating === 2 ? 'Poor' : 'Very Poor'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Feedback Details */}
                <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Feedback Details</h3>
                  
                  {/* Feedback Type */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Type of Feedback *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {feedbackTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = feedbackType === type.id;
                        
                        return (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => setFeedbackType(type.id)}
                            className={`p-3 text-center rounded-lg border-2 transition-all duration-200 ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 bg-white hover:border-gray-300 text-gray-600'
                            }`}
                          >
                            <Icon className="w-5 h-5 mx-auto mb-1" />
                            <div className="text-xs font-medium">{type.name}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Priority Level */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Priority Level *
                    </label>
                    <div className="space-y-2">
                      {priorityLevels.map((level) => {
                        const isSelected = priority === level.id;
                        
                        return (
                          <button
                            key={level.id}
                            type="button"
                            onClick={() => setPriority(level.id)}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                              isSelected
                                ? `border-${level.color}-500 bg-${level.color}-50`
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className={`font-medium ${
                                  isSelected ? `text-${level.color}-900` : 'text-gray-900'
                                }`}>
                                  {level.name}
                                </div>
                                <div className={`text-sm ${
                                  isSelected ? `text-${level.color}-700` : 'text-gray-600'
                                }`}>
                                  {level.description}
                                </div>
                              </div>
                              <div className={`w-4 h-4 rounded-full border-2 ${
                                isSelected
                                  ? `border-${level.color}-500 bg-${level.color}-500`
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <div className="mb-4">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                      Title (Optional)
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Brief summary of your feedback"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Please provide detailed feedback. The more specific you are, the better we can help."
                      rows={6}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    />
                  </div>

                  {/* File Attachments */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.pdf,.txt"
                        className="hidden"
                      />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600 mb-2">
                        Click to upload files or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF, PDF, TXT up to 10MB each (max 5 files)
                      </p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Choose Files
                      </button>
                    </div>
                    
                    {/* Attachment List */}
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-700">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email (Optional)
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Provide your email if you'd like a response from our team
                    </p>
                  </div>
                </div>

                {/* Overall Satisfaction */}
                <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Satisfaction</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {satisfactionOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = satisfactionScore === option.id;
                      
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setSatisfactionScore(option.id)}
                          className={`p-4 text-center rounded-lg border-2 transition-all duration-200 ${
                            isSelected
                              ? `border-${option.color}-500 bg-${option.color}-50`
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-2 ${
                            isSelected ? `text-${option.color}-600` : 'text-gray-400'
                          }`} />
                          <div className={`text-xs font-medium ${
                            isSelected ? `text-${option.color}-900` : 'text-gray-700'
                          }`}>
                            {option.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting || !rating || !description.trim()}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;